import { useState, useRef, useCallback, useEffect } from "react";

// Browser voice-note recorder built on MediaRecorder, mirroring the mobile
// useVoiceRecorder surface (start / stop / cancel + live duration).
//
// Codec note: Safari records audio/mp4 (AAC) which plays everywhere including
// iOS; Chrome/Firefox only record audio/webm (Opus), which iOS AVPlayer cannot
// play. We prefer mp4 when supported and fall back to webm, so voice notes
// recorded in Safari are universally playable and Chrome-recorded ones play on
// web + Android.
function pickMimeType() {
  const candidates = [
    { mime: "audio/mp4", ext: "m4a" },
    { mime: "audio/webm;codecs=opus", ext: "webm" },
    { mime: "audio/webm", ext: "webm" },
  ];
  const MR = typeof window !== "undefined" ? window.MediaRecorder : undefined;
  if (!MR) return null;
  for (const c of candidates) {
    try {
      if (MR.isTypeSupported(c.mime)) return c;
    } catch (_) { /* isTypeSupported can throw on some browsers */ }
  }
  return { mime: "", ext: "webm" }; // let the browser choose
}

export function useWebVoiceRecorder() {
  const supported = typeof window !== "undefined"
    && !!navigator.mediaDevices?.getUserMedia
    && !!window.MediaRecorder;

  const [isRecording, setIsRecording] = useState(false);
  const [durationMillis, setDurationMillis] = useState(0);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);
  const cancelledRef = useRef(false);

  const cleanup = useCallback(() => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
  }, []);

  const start = useCallback(async () => {
    if (!supported) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const picked = pickMimeType();
      const recorder = picked && picked.mime
        ? new MediaRecorder(stream, { mimeType: picked.mime })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      cancelledRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      recorder._pickedExt = picked ? picked.ext : "webm";
      recorderRef.current = recorder;
      streamRef.current = stream;
      startTsRef.current = Date.now();
      setDurationMillis(0);
      recorder.start();
      setIsRecording(true);
      tickRef.current = setInterval(() => setDurationMillis(Date.now() - startTsRef.current), 200);
      return true;
    } catch (err) {
      console.error("Voice recording failed to start:", err);
      cleanup();
      setIsRecording(false);
      return false;
    }
  }, [supported, cleanup]);

  // Resolves with { blob, durationMillis, ext } or null.
  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder) { resolve(null); return; }
      const ext = recorder._pickedExt || "webm";
      const dur = Date.now() - startTsRef.current;
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        cleanup();
        setIsRecording(false);
        setDurationMillis(0);
        resolve(cancelledRef.current || blob.size === 0 ? null : { blob, durationMillis: dur, ext });
      };
      try { recorder.stop(); } catch (_) { cleanup(); setIsRecording(false); resolve(null); }
    });
  }, [cleanup]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const recorder = recorderRef.current;
    if (recorder) { try { recorder.stop(); } catch (_) { /* noop */ } }
    cleanup();
    setIsRecording(false);
    setDurationMillis(0);
  }, [cleanup]);

  // Safety net: stop the mic if the component unmounts mid-recording.
  useEffect(() => () => { cancelledRef.current = true; cleanup(); }, [cleanup]);

  return { supported, isRecording, durationMillis, start, stop, cancel };
}
